import { Device } from '@twilio/voice-sdk'
import { makeAutoObservable, when } from 'mobx'

import type PromptUiStore from '@src/app/prompt'
import type { SoundUiStore } from '@src/app/sound'
import assertNever from '@src/lib/assertNever'
import { DisposeBag } from '@src/lib/dispose'
import { logError } from '@src/lib/log'
import type { IFlagsService } from '@src/service/feature-flags'
import type { VoiceStore } from '@src/service/voice'
import type { ActiveCallFromType, ActiveCallToType } from '@src/service/voice/ActiveCall'

import NetworkInformationUiStore from './NetworkInformationUiStore'
import type UserMediaDeviceUIStore from './user-media-device-store/UserMediaDeviceUIStore'

type UserActivationEventType =
  | 'keydown'
  | 'pointerdown'
  | 'pointerup'
  | 'touchend'
  | 'mousedown'

type UserActivationEvent = KeyboardEvent | PointerEvent | TouchEvent | MouseEvent

export default class VoiceUiStore {
  private static readonly userActivationEventTypes: UserActivationEventType[] = [
    'keydown',
    'pointerdown',
    'pointerup',
    'touchend',
    'mousedown',
  ]
  /**
   * Erring on the side of caution, we are going to ignore all keys that are not printable.
   */
  private static readonly ignoredUserActivationKeys = [
    'Escape',
    'Tab',
    'CapsLock',
    'ScrollLock',
    'Pause',
    'Insert',
  ]
  private readonly disposeBag = new DisposeBag()
  readonly networkInformationUiStore = new NetworkInformationUiStore()

  /**
   * Whether the user has interacted with the page at least once.
   * This is persists for the rest of the window session, but **does reset when the page is reloaded.**
   *
   * **Highly encouraged to treat this value as read-only.**
   *
   * https://developer.mozilla.org/en-US/docs/Web/Security/User_activation#sticky_activation
   */
  private hasBeenActive = false

  constructor(
    private readonly soundUIStore: SoundUiStore,
    private readonly promptUIStore: PromptUiStore,
    private readonly userMediaDeviceUIStore: UserMediaDeviceUIStore,
    private readonly voiceStore: VoiceStore,
    private readonly flagsService: IFlagsService,
  ) {
    makeAutoObservable<this>(this, { tearDown: false }, { autoBind: true })

    VoiceUiStore.userActivationEventTypes.forEach((event) => {
      window.addEventListener(event, this.handleUserActivation, { capture: true })
    })

    this.disposeBag.add(
      /**
       * This is a workaround to start the audio context on the first user interaction
       * when a user's Media Engagement Index (MEI) Score is low for OpenPhone.
       *
       * This is a self-remedying issue, where over time, the MEI score will increase with usage
       * and the user will be able to place and receive calls without needing to interact
       * with the page first.
       *
       * See: https://linear.app/openphone/issue/COM-10213/one-way-audio-issue#comment-fdc8e2ae
       * See: https://developer.chrome.com/blog/autoplay
       *
       */
      when(
        () => this.hasBeenActive,
        () => {
          if (Device.audioContext?.state === 'running') {
            return
          }

          Device.audioContext
            ?.resume()
            ?.then(() => {
              this.voiceStore.attachAudioProcessor()
            })
            .catch(logError)
        },
      ),
      this.unregisterUserActivationListeners,
    )
  }

  init() {
    this.setDefaultSounds()
    return this
  }

  async startCall(from: ActiveCallFromType, to: ActiveCallToType) {
    if (this.flagsService.flags.webImprovedUserMediaDeviceAccess) {
      const hasAccess = await this.requestMicrophoneAccess()
      if (!hasAccess) {
        return
      }
    }
    return this.voiceStore.startCall(from, to)
  }

  async requestMicrophoneAccess() {
    const result = await this.userMediaDeviceUIStore.requestMicrophoneAccess()
    switch (result.type) {
      case 'error':
        this.promptUIStore.show({
          title: result.prompt.title,
          body: result.prompt.message,
          actions: [
            {
              title: 'Retry',
              onClick: () => void this.requestMicrophoneAccess(),
            },
          ],
        })
        return false
      case 'success':
        return true
      default:
        return assertNever(result, 'Unknown result type')
    }
  }

  hasMicrophoneAccess() {
    return this.userMediaDeviceUIStore.hasMicrophoneAccess()
  }

  tearDown() {
    this.disposeBag.dispose()
    this.networkInformationUiStore.tearDown()
  }

  /**
   * Syncs navigator.userActivation state with mobx state.
   * Take this as a best-effort attempt at following the specification due
   * to the highly-ambiguous rules outlined in the specification.
   *
   * See: https://developer.mozilla.org/en-US/docs/Web/Security/User_activation
   */
  private handleUserActivation = (event: UserActivationEvent) => {
    /**
     * We can safely assume that the userActivation state has not changed
     * on non-trusted events.
     */
    if (!event.isTrusted) {
      return
    }

    /**
     * Keyboard events are quite restricted, so we have to make a best-effort attempt at
     * determining if the user has interacted with the page.
     *
     * Firefox: Arrow keys do not count as user-gestures: https://bugzilla.mozilla.org/show_bug.cgi?id=1897649
     *
     */
    if (event instanceof KeyboardEvent) {
      const isPrintableKey = event.key.length === 1
      if (
        !isPrintableKey ||
        event.metaKey ||
        event.shiftKey ||
        event.ctrlKey ||
        event.altKey ||
        VoiceUiStore.ignoredUserActivationKeys.includes(event.key)
      ) {
        return
      }
    }

    if (
      event instanceof PointerEvent &&
      event.type === 'pointerdown' &&
      event.pointerType !== 'mouse'
    ) {
      return
    }

    if (
      event instanceof PointerEvent &&
      event.type === 'pointerup' &&
      event.pointerType === 'mouse'
    ) {
      return
    }

    /**
     * The above code can be removed once we drop support for Safari 16.3 and below.
     * TODO: UXP-4080 — this wont necessarily allow us to drop support for Safari 16.3 right away
     * but it will eventually, once minimum browser requirements have been stated.
     */
    this.hasBeenActive = navigator.userActivation?.hasBeenActive ?? true

    /**
     * We are going to unregister the user activation listeners
     * once the user has interacted with the page at least once.
     * Right now we only use the user activation listeners to start the audio context.
     * Once the audio context is started, we don't need to listen for user activation events.
     */
    if (this.hasBeenActive) {
      this.unregisterUserActivationListeners()
    }
  }

  private setDefaultSounds() {
    this.voiceStore.setDeviceSounds({
      disconnect: this.soundUIStore.url('callEnded'),
      outgoing: this.soundUIStore.url('callStarted'),
      incoming: this.soundUIStore.url('primaryRingtone'),
    })
  }

  private unregisterUserActivationListeners() {
    VoiceUiStore.userActivationEventTypes.forEach((event) => {
      window.removeEventListener(event, this.handleUserActivation, {
        capture: true,
      })
    })
  }
}
